import { useState } from "react";
import { api } from "../../api/client";
import { downloadFile } from "../../api/download";

/** A report file as downloaded: its version per project, report and format, and whether the file is kept. */
export interface ReportVersion {
  id: string; report_key: string; format: string; version_no: number; file_name: string; size_bytes: number;
  run_id: string | null; rules_version: string | null; build_id: string | null; stored: boolean; created_at: string;
}

const size = (b: number) => (b >= 1_048_576 ? `${(b / 1_048_576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);
const when = (iso: string) => new Date(iso).toLocaleString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });

/**
 * Report versions: every report file downloaded, newest first. Downloading a report again with nothing changed
 * returns the same version; any change gives the next. A kept version downloads byte for byte as it was issued.
 */
export function ReportVersions({ projectId }: { projectId: string }) {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState<ReportVersion[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const load = () => {
    setError(null);
    api.get<ReportVersion[]>(`/projects/${projectId}/report-versions`).then((r) => setRows(Array.isArray(r) ? r : [])).catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)));
  };
  const toggle = () => { if (!open) load(); setOpen(!open); };
  const get = (v: ReportVersion) => downloadFile(`/projects/${projectId}/report-versions/${v.id}/file`, v.file_name).catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)));
  return (
    <div className="px-2 py-2 border-t border-ink-100" data-testid="report-versions">
      <button type="button" className="text-[11px] text-ink-700 hover:text-accent" onClick={toggle} aria-expanded={open}>
        {open ? "Hide" : "Show"} report versions
      </button>
      {open && (
        <div className="mt-2 max-h-64 overflow-y-auto">
          {error && <p className="text-[11px] text-red-700" role="alert">{error}</p>}
          {rows && rows.length === 0 && <p className="text-[11px] text-ink-500">No report has been downloaded yet.</p>}
          {rows && rows.length > 0 && (
            <table className="w-full text-[11px]">
              <thead><tr className="text-left text-ink-500"><th className="py-1">File</th><th>Version</th><th>Issued</th><th>Size</th><th /></tr></thead>
              <tbody>
                {rows.map((v) => (
                  <tr key={v.id} className="border-t border-ink-100" data-testid="report-version-row">
                    <td className="py-1 pr-2 break-all">{v.file_name}</td>
                    <td className="pr-2">v{v.version_no}</td>
                    <td className="pr-2 whitespace-nowrap">{when(v.created_at)}</td>
                    <td className="pr-2 whitespace-nowrap">{size(v.size_bytes)}</td>
                    <td>{v.stored
                      ? <button type="button" className="text-accent hover:underline" onClick={() => void get(v)}>Download</button>
                      : <span className="text-ink-500" title="Too large to keep; download the report again to rebuild it">not kept</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}
